import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

import { EventTrack } from './eventMock';
import EventPanel from './EventPanel';
import { EVENT_FONT_CONDENSED, eventColors } from './eventTheme';
import GreenButton from './GreenButton';
import MilestoneRail, { milestoneStateAt } from './MilestoneRail';
import RewardTile from './RewardTile';

// Figma "Free track / reward milestones" (3324:18942) and "Premium track" (3324:18957): a
// 360-wide panel with a 344-wide content column - heading, milestone rail, the reward tiles the
// rail's nodes sit above, and a CLAIM pill centred under the tile that is ready to collect.
export const TRACK_DESIGN_WIDTH = 360;
const PANEL_PADDING = 8;
const PAD_TOP = 12;
const HEADING_HEIGHT = 30;
const RAIL_GAP = 6;
const RAIL_HEIGHT = 44;
const REWARDS_GAP = 6;
const CLAIM_GAP = 5;
const CLAIM_HEIGHT = 20;
const PAD_BOTTOM = 7;

type Props = {
	track: EventTrack;
	width: number;
	onClaim?: (track: EventTrack) => void;
};

export const EventTrackCard = ({ track, width, onClaim }: Props) => {
	const { t } = useTranslation();
	const scale = width / TRACK_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale), [scale]);

	const innerWidth = (TRACK_DESIGN_WIDTH - PANEL_PADDING * 2) * scale;
	const tileWidth = track.tileWidth * scale;
	const tileHeight = track.tileHeight * scale;
	const pitch = (track.tileWidth + track.tileGap) * scale;
	// A rail node sits on the centre of the tile it unlocks, which is what lines the two rows up.
	const centers = track.rewards.map((_, index) => tileWidth / 2 + pitch * index);

	const panelHeight =
		(PAD_TOP + HEADING_HEIGHT + RAIL_GAP + RAIL_HEIGHT + REWARDS_GAP) * scale +
		tileHeight +
		(CLAIM_GAP + CLAIM_HEIGHT + PAD_BOTTOM) * scale;

	const claimWidth = Math.max(tileWidth, 46 * scale);
	const claimLeft = centers[track.currentIndex] - claimWidth / 2;

	return (
		<EventPanel
			radius={10 * scale}
			borderWidth={Math.max(1, 1 * scale)}
			style={{ width, height: panelHeight }}
		>
			<View style={styles.inner}>
				<View style={styles.heading}>
					<Image source={track.icon} style={styles.headingIcon} resizeMode='contain' />
					<Text numberOfLines={1} style={styles.headingLabel}>
						{track.title}
					</Text>
				</View>

				<View style={styles.railRow}>
					<MilestoneRail
						milestones={track.milestones}
						currentIndex={track.currentIndex}
						centers={centers}
						scale={scale}
					/>
				</View>

				<View style={[styles.rewards, { width: innerWidth, height: tileHeight }]}>
					{track.rewards.map((reward, index) => {
						const state = milestoneStateAt(index, track.currentIndex);
						return (
							<RewardTile
								key={reward.id}
								reward={reward}
								width={tileWidth}
								height={tileHeight}
								claimable={state === 'current' && track.claimable}
								dimmed={state === 'locked' || Boolean(track.locked)}
								scale={scale}
							/>
						);
					})}
				</View>

				<View style={styles.claimRow}>
					{track.claimable ? (
						<GreenButton
							label={t('T_SITE_MISSIONS_CLAIM', { defaultValue: 'CLAIM' })}
							onPress={() => onClaim?.(track)}
							width={claimWidth}
							height={CLAIM_HEIGHT * scale}
							fontSize={11 * scale}
							radius={5 * scale}
							borderWidth={Math.max(1, 1 * scale)}
							style={{ marginLeft: Math.max(0, claimLeft) }}
						/>
					) : null}
				</View>
			</View>
		</EventPanel>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		inner: {
			flex: 1,
			paddingHorizontal: PANEL_PADDING * scale,
			paddingTop: PAD_TOP * scale,
		},
		heading: {
			height: HEADING_HEIGHT * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: 8 * scale,
		},
		headingIcon: {
			width: 24 * scale,
			height: 24 * scale,
		},
		// Figma "Card/Title": Sofia Sans Condensed SemiBold 16. The app bundles Semi Condensed,
		// the substitution DailyMissionRow already makes for the same token.
		headingLabel: {
			color: eventColors.accent,
			fontFamily: EVENT_FONT_CONDENSED,
			fontSize: 16 * scale,
			fontWeight: '600',
			letterSpacing: 0.5 * scale,
		},
		railRow: {
			marginTop: RAIL_GAP * scale,
		},
		rewards: {
			marginTop: REWARDS_GAP * scale,
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		claimRow: {
			marginTop: CLAIM_GAP * scale,
			height: CLAIM_HEIGHT * scale,
			flexDirection: 'row',
		},
	});

export default EventTrackCard;
